import type { Order } from "~/v2/api/rest/account";

export type ReplacementChainIssueCode =
  | "asymmetric-link"
  | "conflicting-link"
  | "cycle"
  | "depth-limit"
  | "duplicate-id"
  | "fetch-failed"
  | "invalid-id"
  | "invalid-link"
  | "missing-reference"
  | "rejected-attempt";

export interface ReplacementChainIssue {
  code: ReplacementChainIssueCode;
  message: string;
  orderID?: string;
  referencedOrderID?: string;
}

export interface ReplacementHistory {
  /** Deterministic primary earliest-loaded → latest-loaded sequence. */
  versions: Order[];
  /** Rejected children that the original did not reciprocally confirm. */
  rejectedAttempts: Order[];
  /** Loaded branch records that cannot be placed on the primary sequence. */
  ambiguousVersions: Order[];
  /** Best loaded head candidate for the primary sequence. */
  headOrder: Order;
  /** False when a newer or unambiguous current version could not be verified. */
  isHeadKnown: boolean;
  /** False when an earlier predecessor or unambiguous root cannot be ruled out. */
  isRootKnown: boolean;
  clickedOrderID: string;
  issues: ReplacementChainIssue[];
  isComplete: boolean;
}

interface ReplacementNode {
  key: string;
  id?: string;
  index: number;
  order: Order;
}

interface ReplacementRelation {
  parentID: string;
  childID: string;
  /** The parent says `replaced_by: child`. */
  hasForwardPointer: boolean;
  /** The child says `replaces: parent`. */
  hasBackwardPointer: boolean;
}

interface ComponentAnalysis {
  orders: Order[];
  headOrder: Order;
  isHeadKnown: boolean;
  issues: ReplacementChainIssue[];
  detachedRejectedIDs: Set<string>;
  relations: ReplacementRelation[];
}

const normalizeID = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const hasProvidedValue = (value: unknown): boolean =>
  value !== undefined && value !== null;

const normalizedStatus = (order: Order): string =>
  typeof order.status === "string" ? order.status.trim().toLowerCase() : "";

const getOrderTimestamp = (order: Order): number => {
  for (const value of [
    order.submitted_at,
    order.created_at,
    order.updated_at,
  ]) {
    if (typeof value !== "string") {
      continue;
    }
    const timestamp = Date.parse(value);
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
  }
  return Number.NEGATIVE_INFINITY;
};

const issueKey = (issue: ReplacementChainIssue): string =>
  [
    issue.code,
    issue.orderID ?? "",
    issue.referencedOrderID ?? "",
    issue.message,
  ].join(":");

const dedupeIssues = (
  issues: readonly ReplacementChainIssue[],
): ReplacementChainIssue[] => {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    const key = issueKey(issue);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const compareHeadCandidates = (
  first: ReplacementNode,
  second: ReplacementNode,
): number => {
  const statusScore = (node: ReplacementNode) => {
    const status = normalizedStatus(node.order);
    if (status === "replaced") {
      return 0;
    }
    if (status === "rejected") {
      return 1;
    }
    return 2;
  };
  const scoreDifference = statusScore(second) - statusScore(first);
  if (scoreDifference !== 0) {
    return scoreDifference;
  }

  const timestampDifference =
    getOrderTimestamp(second.order) - getOrderTimestamp(first.order);
  if (timestampDifference !== 0) {
    return timestampDifference;
  }

  return (first.id ?? first.key).localeCompare(second.id ?? second.key);
};

const buildNodes = (orders: readonly Order[]) => {
  const nodes = orders.map((order, index): ReplacementNode => {
    const id = normalizeID(order.id);
    return {
      key: id ? `id:${id}:${index}` : `page:${index}`,
      id,
      index,
      order,
    };
  });
  const nodesByID = new Map<string, ReplacementNode>();
  const duplicateIDs = new Set<string>();

  for (const node of nodes) {
    if (!node.id) {
      continue;
    }
    if (nodesByID.has(node.id)) {
      duplicateIDs.add(node.id);
      continue;
    }
    nodesByID.set(node.id, node);
  }

  return { duplicateIDs, nodes, nodesByID };
};

const connectNodes = (
  adjacency: Map<string, Set<string>>,
  first: ReplacementNode,
  second: ReplacementNode,
) => {
  if (first.key === second.key) {
    return;
  }
  adjacency.get(first.key)?.add(second.key);
  adjacency.get(second.key)?.add(first.key);
};

const buildNodeAdjacency = (
  nodes: ReplacementNode[],
  nodesByID: ReadonlyMap<string, ReplacementNode>,
): Map<string, Set<string>> => {
  const adjacency = new Map(nodes.map((node) => [node.key, new Set<string>()]));
  for (const node of nodes) {
    for (const reference of [
      normalizeID(node.order.replaces),
      normalizeID(node.order.replaced_by),
    ]) {
      const referencedNode = reference ? nodesByID.get(reference) : undefined;
      if (node.id && referencedNode) {
        connectNodes(adjacency, node, referencedNode);
      }
    }
  }
  return adjacency;
};

const collectConnectedGroup = (
  startKey: string,
  adjacency: ReadonlyMap<string, ReadonlySet<string>>,
  nodesByKey: ReadonlyMap<string, ReplacementNode>,
  visited: Set<string>,
): ReplacementNode[] => {
  const group: ReplacementNode[] = [];
  const pending = [startKey];
  while (pending.length > 0) {
    const key = pending.pop();
    if (!key || visited.has(key)) {
      continue;
    }
    visited.add(key);
    const current = nodesByKey.get(key);
    if (current) {
      group.push(current);
    }
    for (const adjacentKey of adjacency.get(key) ?? []) {
      if (!visited.has(adjacentKey)) {
        pending.push(adjacentKey);
      }
    }
  }
  return group.sort((first, second) => first.index - second.index);
};

const buildConnectedNodeGroups = (
  nodes: ReplacementNode[],
  nodesByID: ReadonlyMap<string, ReplacementNode>,
): ReplacementNode[][] => {
  const adjacency = buildNodeAdjacency(nodes, nodesByID);
  const nodesByKey = new Map(nodes.map((node) => [node.key, node]));
  const visited = new Set<string>();
  const groups: ReplacementNode[][] = [];
  for (const node of nodes) {
    if (!visited.has(node.key)) {
      groups.push(
        collectConnectedGroup(node.key, adjacency, nodesByKey, visited),
      );
    }
  }
  return groups;
};

type PointerDirection = "forward" | "backward";

const addRelation = (
  relationByKey: Map<string, ReplacementRelation>,
  parentID: string,
  childID: string,
  direction: PointerDirection,
) => {
  const key = `${parentID}\0${childID}`;
  const relation = relationByKey.get(key) ?? {
    parentID,
    childID,
    hasForwardPointer: false,
    hasBackwardPointer: false,
  };
  if (direction === "forward") {
    relation.hasForwardPointer = true;
  } else {
    relation.hasBackwardPointer = true;
  }
  relationByKey.set(key, relation);
};

const inspectPointer = ({
  direction,
  issues,
  node,
  nodesByID,
  rawReference,
  relationByKey,
}: {
  direction: PointerDirection;
  issues: ReplacementChainIssue[];
  node: ReplacementNode & { id: string };
  nodesByID: ReadonlyMap<string, ReplacementNode>;
  rawReference: unknown;
  relationByKey: Map<string, ReplacementRelation>;
}) => {
  const referencedOrderID = normalizeID(rawReference);
  const pointerName = direction === "backward" ? "replaces" : "replaced_by";
  if (!referencedOrderID) {
    if (hasProvidedValue(rawReference)) {
      issues.push({
        code: "invalid-link",
        orderID: node.id,
        message: `Order ${node.id} has an invalid ${pointerName} link.`,
      });
    }
    return;
  }
  if (referencedOrderID === node.id) {
    issues.push({
      code: "cycle",
      orderID: node.id,
      referencedOrderID,
      message:
        direction === "backward"
          ? `Order ${node.id} points to itself as the version it replaces.`
          : `Order ${node.id} points to itself as its replacement.`,
    });
    return;
  }
  if (!nodesByID.has(referencedOrderID)) {
    issues.push({
      code: "missing-reference",
      orderID: node.id,
      referencedOrderID,
      message:
        direction === "backward"
          ? `Prior order version ${referencedOrderID} is not loaded.`
          : `Newer order version ${referencedOrderID} is not loaded.`,
    });
    return;
  }
  addRelation(
    relationByKey,
    direction === "backward" ? referencedOrderID : node.id,
    direction === "backward" ? node.id : referencedOrderID,
    direction,
  );
};

const collectComponentRelations = ({
  componentIDs,
  duplicateIDs,
  issues,
  nodes,
  nodesByID,
}: {
  componentIDs: ReadonlySet<string>;
  duplicateIDs: ReadonlySet<string>;
  issues: ReplacementChainIssue[];
  nodes: ReplacementNode[];
  nodesByID: ReadonlyMap<string, ReplacementNode>;
}): ReplacementRelation[] => {
  const relationByKey = new Map<string, ReplacementRelation>();
  for (const node of nodes) {
    if (!node.id) {
      issues.push({
        code: "invalid-id",
        message:
          "A loaded order has no usable ID, so it cannot be joined to replacement versions.",
      });
      continue;
    }
    if (duplicateIDs.has(node.id)) {
      issues.push({
        code: "duplicate-id",
        orderID: node.id,
        message: `Order ${node.id} appears more than once in the loaded replacement history.`,
      });
    }
    const identifiedNode = { ...node, id: node.id };
    inspectPointer({
      direction: "backward",
      issues,
      node: identifiedNode,
      nodesByID,
      rawReference: node.order.replaces,
      relationByKey,
    });
    inspectPointer({
      direction: "forward",
      issues,
      node: identifiedNode,
      nodesByID,
      rawReference: node.order.replaced_by,
      relationByKey,
    });
  }
  return [...relationByKey.values()].filter(
    ({ parentID, childID }) =>
      componentIDs.has(parentID) && componentIDs.has(childID),
  );
};

const classifyOneWayRelations = (
  relations: readonly ReplacementRelation[],
  nodesByID: ReadonlyMap<string, ReplacementNode>,
  issues: ReplacementChainIssue[],
): Set<string> => {
  const detachedRejectedIDs = new Set<string>();
  for (const relation of relations) {
    if (relation.hasForwardPointer && relation.hasBackwardPointer) {
      continue;
    }
    const parent = nodesByID.get(relation.parentID)?.order;
    const child = nodesByID.get(relation.childID)?.order;
    const parentForwardID = normalizeID(parent?.replaced_by);
    const childBackwardID = normalizeID(child?.replaces);
    if (
      relation.hasBackwardPointer &&
      !relation.hasForwardPointer &&
      child &&
      normalizedStatus(child) === "rejected" &&
      !parentForwardID
    ) {
      detachedRejectedIDs.add(relation.childID);
      issues.push({
        code: "rejected-attempt",
        orderID: relation.childID,
        referencedOrderID: relation.parentID,
        message: `Rejected order ${relation.childID} points back to ${relation.parentID}, but the original did not confirm it as a replacement.`,
      });
      continue;
    }
    const isConflict =
      (relation.hasBackwardPointer &&
        Boolean(parentForwardID) &&
        parentForwardID !== relation.childID) ||
      (relation.hasForwardPointer &&
        Boolean(childBackwardID) &&
        childBackwardID !== relation.parentID);
    issues.push({
      code: isConflict ? "conflicting-link" : "asymmetric-link",
      orderID: relation.childID,
      referencedOrderID: relation.parentID,
      message: isConflict
        ? `Orders ${relation.parentID} and ${relation.childID} have conflicting replacement links.`
        : `Orders ${relation.parentID} and ${relation.childID} have a one-way replacement link.`,
    });
  }
  return detachedRejectedIDs;
};

interface RelationIndexes {
  incoming: Map<string, Set<string>>;
  outgoing: Map<string, Set<string>>;
}

const buildRelationIndexes = (
  relations: readonly ReplacementRelation[],
): RelationIndexes => {
  const outgoing = new Map<string, Set<string>>();
  const incoming = new Map<string, Set<string>>();
  for (const relation of relations) {
    const successors = outgoing.get(relation.parentID) ?? new Set<string>();
    successors.add(relation.childID);
    outgoing.set(relation.parentID, successors);
    const predecessors = incoming.get(relation.childID) ?? new Set<string>();
    predecessors.add(relation.parentID);
    incoming.set(relation.childID, predecessors);
  }
  return { incoming, outgoing };
};

const collectBranchIssues = (
  { incoming, outgoing }: RelationIndexes,
  issues: ReplacementChainIssue[],
) => {
  for (const [orderID, successors] of outgoing) {
    if (successors.size > 1) {
      issues.push({
        code: "conflicting-link",
        orderID,
        message: `Order ${orderID} links to multiple replacement versions.`,
      });
    }
  }
  for (const [orderID, predecessors] of incoming) {
    if (predecessors.size > 1) {
      issues.push({
        code: "conflicting-link",
        orderID,
        message: `Order ${orderID} has multiple possible prior versions.`,
      });
    }
  }
};

const visitForCycles = (
  orderID: string,
  outgoing: ReadonlyMap<string, ReadonlySet<string>>,
  visited: Set<string>,
  active: Set<string>,
  cycleIDs: Set<string>,
) => {
  if (active.has(orderID)) {
    cycleIDs.add(orderID);
    return;
  }
  if (visited.has(orderID)) {
    return;
  }
  visited.add(orderID);
  active.add(orderID);
  for (const successor of outgoing.get(orderID) ?? []) {
    visitForCycles(successor, outgoing, visited, active, cycleIDs);
  }
  active.delete(orderID);
};

const collectCycleIssue = (
  componentIDs: ReadonlySet<string>,
  outgoing: ReadonlyMap<string, ReadonlySet<string>>,
  issues: ReplacementChainIssue[],
) => {
  const visited = new Set<string>();
  const active = new Set<string>();
  const cycleIDs = new Set<string>();
  for (const orderID of componentIDs) {
    visitForCycles(orderID, outgoing, visited, active, cycleIDs);
  }
  if (cycleIDs.size > 0) {
    issues.push({
      code: "cycle",
      orderID: [...cycleIDs].sort()[0],
      message: "A cycle was detected in the loaded replacement links.",
    });
  }
};

interface HistoryHeadSelection {
  headNode: ReplacementNode;
  isHeadKnown: boolean;
}

const selectHistoryHead = ({
  detachedRejectedIDs,
  issues,
  nodes,
  nodesByID,
  outgoing,
}: {
  detachedRejectedIDs: ReadonlySet<string>;
  issues: readonly ReplacementChainIssue[];
  nodes: ReplacementNode[];
  nodesByID: ReadonlyMap<string, ReplacementNode>;
  outgoing: ReadonlyMap<string, ReadonlySet<string>>;
}): HistoryHeadSelection => {
  const nonDetachedNodes = nodes.filter(
    (node) => !node.id || !detachedRejectedIDs.has(node.id),
  );
  const candidateNodes = nonDetachedNodes.length > 0 ? nonDetachedNodes : nodes;
  const candidateIDs = new Set(
    candidateNodes.flatMap((node) => (node.id ? [node.id] : [])),
  );
  const headCandidates = candidateNodes.filter(
    (node) =>
      !node.id ||
      ![...(outgoing.get(node.id) ?? [])].some(
        (successor) =>
          candidateIDs.has(successor) && !detachedRejectedIDs.has(successor),
      ),
  );
  const headNode =
    [...(headCandidates.length > 0 ? headCandidates : candidateNodes)].sort(
      compareHeadCandidates,
    )[0] ?? nodes[0];
  const hasAmbiguousStructure = issues.some(
    ({ code }) =>
      code === "conflicting-link" ||
      code === "cycle" ||
      code === "duplicate-id" ||
      code === "invalid-id",
  );
  const hasMissingNewerVersion = nodes.some((node) => {
    const forwardID = normalizeID(node.order.replaced_by);
    return Boolean(forwardID && !nodesByID.has(forwardID));
  });
  const hasInvalidNewerVersion = nodes.some(
    (node) =>
      hasProvidedValue(node.order.replaced_by) &&
      !normalizeID(node.order.replaced_by),
  );
  const isHeadKnown =
    Boolean(headNode.id) &&
    !hasAmbiguousStructure &&
    !hasMissingNewerVersion &&
    !hasInvalidNewerVersion;
  return {
    headNode,
    isHeadKnown,
  };
};

const analyzeComponent = ({
  duplicateIDs,
  nodes,
  nodesByID,
}: {
  duplicateIDs: ReadonlySet<string>;
  nodes: ReplacementNode[];
  nodesByID: ReadonlyMap<string, ReplacementNode>;
}): ComponentAnalysis => {
  const issues: ReplacementChainIssue[] = [];
  const componentIDs = new Set(
    nodes.flatMap((node) => (node.id ? [node.id] : [])),
  );
  const relations = collectComponentRelations({
    componentIDs,
    duplicateIDs,
    issues,
    nodes,
    nodesByID,
  });
  const detachedRejectedIDs = classifyOneWayRelations(
    relations,
    nodesByID,
    issues,
  );
  const relationIndexes = buildRelationIndexes(relations);
  collectBranchIssues(relationIndexes, issues);
  collectCycleIssue(componentIDs, relationIndexes.outgoing, issues);
  const selection = selectHistoryHead({
    detachedRejectedIDs,
    issues,
    nodes,
    nodesByID,
    outgoing: relationIndexes.outgoing,
  });

  return {
    orders: nodes.map(({ order }) => order),
    headOrder: selection.headNode.order,
    isHeadKnown: selection.isHeadKnown,
    issues: dedupeIssues(issues),
    detachedRejectedIDs,
    relations,
  };
};

const analyzeOrders = (orders: readonly Order[]): ComponentAnalysis[] => {
  const { duplicateIDs, nodes, nodesByID } = buildNodes(orders);
  return buildConnectedNodeGroups(nodes, nodesByID).map((componentNodes) =>
    analyzeComponent({
      duplicateIDs,
      nodes: componentNodes,
      nodesByID,
    }),
  );
};

const compareHistoryOrders = (first: Order, second: Order): number => {
  const timestampDifference =
    getOrderTimestamp(first) - getOrderTimestamp(second);
  if (timestampDifference !== 0) {
    return timestampDifference;
  }
  return (normalizeID(first.id) ?? "").localeCompare(
    normalizeID(second.id) ?? "",
  );
};

/**
 * Builds a truthful primary sequence from already-loaded detail records.
 * Conflicting branches and detached rejected attempts remain visible outside
 * the primary path rather than being silently discarded.
 */
export const buildReplacementHistory = (
  orders: readonly Order[],
  clickedOrderID: string,
  additionalIssues: readonly ReplacementChainIssue[] = [],
): ReplacementHistory | undefined => {
  if (orders.length === 0) {
    return undefined;
  }

  const normalizedClickedID = normalizeID(clickedOrderID);
  const analyses = analyzeOrders(orders);
  const analysis =
    analyses.find((candidate) =>
      candidate.orders.some(
        (order) => normalizeID(order.id) === normalizedClickedID,
      ),
    ) ?? analyses[0];
  if (!analysis) {
    return undefined;
  }

  const orderByID = new Map(
    analysis.orders.flatMap((order) => {
      const id = normalizeID(order.id);
      return id ? [[id, order] as const] : [];
    }),
  );
  const headID = normalizeID(analysis.headOrder.id);
  const relationStrength = (relation: ReplacementRelation): number =>
    Number(relation.hasForwardPointer) + Number(relation.hasBackwardPointer);
  const incoming = new Map<string, ReplacementRelation[]>();
  for (const relation of analysis.relations) {
    if (analysis.detachedRejectedIDs.has(relation.childID)) {
      continue;
    }
    const current = incoming.get(relation.childID) ?? [];
    current.push(relation);
    incoming.set(relation.childID, current);
  }

  const pathIDs: string[] = [];
  const pathVisited = new Set<string>();
  let currentID = headID;
  while (currentID && !pathVisited.has(currentID)) {
    pathVisited.add(currentID);
    pathIDs.push(currentID);
    const predecessors = [...(incoming.get(currentID) ?? [])].sort(
      (first, second) => {
        const strengthDifference =
          relationStrength(second) - relationStrength(first);
        if (strengthDifference !== 0) {
          return strengthDifference;
        }
        const firstOrder = orderByID.get(first.parentID);
        const secondOrder = orderByID.get(second.parentID);
        if (firstOrder && secondOrder) {
          const orderDifference = compareHistoryOrders(secondOrder, firstOrder);
          if (orderDifference !== 0) {
            return orderDifference;
          }
        }
        return first.parentID.localeCompare(second.parentID);
      },
    );
    currentID = predecessors[0]?.parentID;
  }

  const versions = pathIDs.reverse().flatMap((id) => {
    const order = orderByID.get(id);
    return order ? [order] : [];
  });
  const versionIDs = new Set(
    versions.flatMap((order) => {
      const id = normalizeID(order.id);
      return id ? [id] : [];
    }),
  );
  const rejectedAttempts = analysis.orders
    .filter((order) => {
      const id = normalizeID(order.id);
      return Boolean(id && analysis.detachedRejectedIDs.has(id));
    })
    .sort(compareHistoryOrders);
  const rejectedAttemptIDs = new Set(
    rejectedAttempts.flatMap((order) => {
      const id = normalizeID(order.id);
      return id ? [id] : [];
    }),
  );
  const ambiguousVersions = analysis.orders
    .filter((order) => {
      const id = normalizeID(order.id);
      return Boolean(
        !id || (!versionIDs.has(id) && !rejectedAttemptIDs.has(id)),
      );
    })
    .sort(compareHistoryOrders);
  const issues = dedupeIssues([...analysis.issues, ...additionalIssues]);
  const primaryVersions = versions.length > 0 ? versions : [analysis.headOrder];
  const rootOrder = primaryVersions[0];
  const rootOrderID = normalizeID(rootOrder?.id);
  const primaryOrderIDs = new Set(
    analysis.orders.flatMap((order) => {
      const id = normalizeID(order.id);
      return id && !analysis.detachedRejectedIDs.has(id) ? [id] : [];
    }),
  );
  const loadedPredecessorIDs = new Set(
    analysis.relations.flatMap(({ childID }) =>
      primaryOrderIDs.has(childID) ? [childID] : [],
    ),
  );
  const loadedRootCandidates = [...primaryOrderIDs].filter(
    (orderID) => !loadedPredecessorIDs.has(orderID),
  );
  const hasCyclicStructure = issues.some(({ code }) => code === "cycle");
  const hasRootIdentityIssue = issues.some(
    ({ code, orderID }) =>
      orderID === rootOrderID &&
      (code === "duplicate-id" || code === "invalid-id"),
  );
  const isRootKnown =
    Boolean(rootOrderID) &&
    !hasProvidedValue(rootOrder?.replaces) &&
    loadedRootCandidates.length === 1 &&
    loadedRootCandidates[0] === rootOrderID &&
    !hasCyclicStructure &&
    !hasRootIdentityIssue;

  return {
    versions: primaryVersions,
    rejectedAttempts,
    ambiguousVersions,
    headOrder: analysis.headOrder,
    isHeadKnown: analysis.isHeadKnown,
    isRootKnown,
    clickedOrderID:
      normalizedClickedID ??
      normalizeID(analysis.headOrder.id) ??
      clickedOrderID,
    issues,
    isComplete: issues.length === 0,
  };
};
